import { GitPullRequestArrowIcon, Link2Icon } from 'lucide-react';

import { ExternalLink } from '@/shared/components/ExternalLink';
import { hostOf, isWebAddress } from '@/shared/components/webAddress';
import type { ResultLink, ResultLinkKind } from '@/lib/api/generated/model';

/**
 * Pull requests first, merge requests second, everything else last (spec 095
 * FR-011). A `Record` over the kind, so a kind the contract adds later fails to
 * compile here rather than sorting last in silence.
 */
const KIND_ORDER: Record<ResultLinkKind, number> = {
  pull_request: 0,
  merge_request: 1,
  other: 2,
};

const KIND_ICON: Record<ResultLinkKind, typeof Link2Icon> = {
  pull_request: GitPullRequestArrowIcon,
  merge_request: GitPullRequestArrowIcon,
  other: Link2Icon,
};
const KIND_WORD: Record<ResultLinkKind, string | null> = {
  pull_request: 'pull request',
  merge_request: 'merge request',
  other: null,
};

/**
 * The links a run proved it made. These are the only anchors on the Results
 * tab: the summary above them renders its own addresses as text, because an
 * address in prose may be a guess and these were checked against the run's
 * own output before they were stored.
 *
 * Nothing when the list is empty (FR-017): a "Links" heading over nothing
 * would say the run should have made some.
 */
export function ResultLinks({ links }: { links: readonly ResultLink[] }) {
  if (links.length === 0) return null;
  const ordered = [...links].sort(
    (a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind],
  );
  return (
    <ul aria-label="Links" className="flex flex-col gap-1 text-sm">
      {ordered.map((link) => {
        // A nameless link gets its host (FR-012); a link that is not a web
        // address is shown but never activated, which is `ExternalLink`'s
        // precondition stated at the one place that could break it.
        const label = link.label.trim() || safeHost(link.url);
        const kindWord = KIND_WORD[link.kind];
        const Icon = KIND_ICON[link.kind];
        return (
          <li
            key={`${link.kind}:${link.url}`}
            className="flex min-w-0 flex-wrap items-baseline gap-x-2"
          >
            <Icon
              aria-hidden="true"
              className="text-muted-foreground size-3.5 shrink-0 self-center"
            />
            {isWebAddress(link.url) ? (
              <ExternalLink href={link.url}>{label}</ExternalLink>
            ) : (
              <span className="break-all">{label}</span>
            )}
            {kindWord ? (
              <span className="text-muted-foreground text-xs">{kindWord}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function safeHost(url: string): string {
  return isWebAddress(url) ? hostOf(url) : url;
}
